"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { useMemo } from "react";

/**
 * Key Numbers 1036 — BDU Programme key features.
 *
 * Port of BDU v1 `components/Degree/KeyFeatures.tsx`: a solid bar of circled-icon
 * columns, each with one line of copy ("2 Years Duration", "5 Electives", …).
 *
 * LAYOUT — the reference is a two-column grid below 860px, a fixed 200px auto-fit
 * track from 860px, and a `1fr` stretched track from 1024px, with a hairline
 * `::after` rule to the right of every column except the last.
 *
 * BREAKPOINTS — none of the source's thresholds map onto this app's scale. 860px is a
 * raw pixel value in the source; `md:` there is 768px (this app's `tab:`, not its
 * remapped 1230px `md:`); and `lg:` there is Tailwind's default 1024px, whereas this
 * app remaps `lg:` to 1430px. So the source's `lg:` chain is written as
 * `min-[1024px]:` here — verified against the deployed bundle, where `.lg\:px-11` and
 * `.lg\:grid-cols-[…1fr]` both sit inside `@media (min-width:1024px)`. Copying `lg:`
 * verbatim left 1024–1429px on the narrow 16px gutter with fixed-width columns.
 *
 * An odd final card spans both columns and centres itself below 860px — reproduced.
 */

interface FeatureRow {
  id?: string | number;
  meta_icon?: string;
  meta_icon_alt?: string;
  /** The single line of copy, e.g. "Live & Recorded Lectures" */
  meta_description?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface KeyNumbers1036Data {
  metadata?: FeatureRow[];
  /** Bar background */
  background_color?: string;
  /** Copy colour */
  description_color?: string;
  /** Icon circle fill */
  gradient_color_1?: string;
  /** Hairline rule between columns */
  gradient_color_4?: string;
  status?: boolean;
}

const KeyNumbers1036 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as KeyNumbers1036Data | undefined;

  const {
    background_color = "#836537",
    description_color = "#FFFFFF",
    gradient_color_1 = "#FFFFFF",
    gradient_color_4 = "#FFFFFF66",
  } = sectionData || {};

  const features = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [];
    return rows
      .filter((r) => r?.meta_status !== false)
      .sort(
        (a, b) =>
          (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  if (!sectionData?.status || features.length === 0) return null;

  const isOddCount = features.length % 2 !== 0;

  return (
    <div
      className={cn(
        "grid w-full grid-cols-2 items-start justify-center gap-8 px-[18px] py-5",
        "min-[860px]:grid-cols-[repeat(auto-fit,minmax(200px,200px))] min-[860px]:gap-12 min-[860px]:px-4 min-[860px]:py-7",
        "min-[1024px]:grid-cols-[repeat(auto-fit,minmax(200px,1fr))] min-[1024px]:px-11",
      )}
      style={{ backgroundColor: background_color }}
    >
      {features.map((feature, index) => {
        const isLast = index === features.length - 1;
        return (
          <div
            key={feature.id ?? index}
            className={cn(
              "relative flex h-full flex-1 flex-col items-center justify-start gap-2 tab:py-4",
              isOddCount &&
                isLast &&
                "col-span-2 mx-auto max-w-[200px] min-[860px]:col-span-1",
            )}
          >
            <div
              className="flex items-center justify-center rounded-full p-[18px]"
              style={{ backgroundColor: gradient_color_1 }}
            >
              {feature.meta_icon && (
                <Image
                  src={feature.meta_icon}
                  alt={feature.meta_icon_alt || "Icon"}
                  width={36}
                  height={36}
                  loading="lazy"
                />
              )}
            </div>

            <p
              className="text-center text-sm tab:text-lg"
              style={{ color: description_color }}
            >
              {feature.meta_description}
            </p>

            {/*
              The reference draws the column rule with an `::after` pseudo-element
              offset -24px (half the 48px gap). An arbitrary-variant `after:` chain
              would need a colour baked into the class name, which cannot be
              CMS-driven, so it is an absolutely positioned element instead.
            */}
            {!isLast && (
              <span
                aria-hidden
                className="absolute -right-6 top-1/2 hidden h-24 w-px -translate-y-1/2 min-[860px]:block"
                style={{ backgroundColor: gradient_color_4 }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default KeyNumbers1036;
